import { getData, postData } from './FetchNodeServices';

export interface ApprovalRule {
  id: string;
  name: string;
  minAmount: number;
  maxAmount: number | null;
  approverRole: string;
  approverName: string;
  approverId: string;
  timeoutHours: number;
  escalateTo: string | null;
  isActive: boolean;
}

export interface ApprovalRequest {
  id: string;
  poId: string;
  poNumber: string;
  poAmount: number;
  supplierName: string;
  ruleId: string;
  approverRole: string;
  approverName: string;
  approverId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SENT_BACK' | 'ESCALATED' | 'SUPERSEDED';
  requestedBy: string;
  requestedAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  timeoutAt: string | null;
  comments: string | null;
  items?: Array<{
    id: string;
    itemName: string;
    qty: number;
    unit?: string;
    rate: number;
    total: number;
  }>;
}

export interface ApprovalStats {
  pending: number;
  approvedToday: number;
  rejected: number;
  avgHours: string;
}

export interface AuditEvent {
  id: string;
  dateTime: string;
  poId?: string;
  poNumber: string;
  amount: number;
  action: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'SENT_BACK' | 'ESCALATED' | 'AUTO_APPROVED';
  performedBy: string;
  role: string;
  comments: string | null;
}

export const purchaseApprovalService = {
  // Fetch approval requests with status, role, search, and warehouseId filters
  getRequests: async (params?: { status?: string; role?: string; search?: string; warehouseId?: string }): Promise<ApprovalRequest[]> => {
    const res = await getData('api/v1/purchase-approval', params);
    if (res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  },

  // Fetch summary stats for dashboard
  getStats: async (params?: { warehouseId?: string }): Promise<ApprovalStats> => {
    const res = await getData('api/v1/purchase-approval/stats', params);
    if (res && res.data) {
      return res.data;
    }
    return { pending: 0, approvedToday: 0, rejected: 0, avgHours: '—' };
  },

  // Fetch audit trail history
  getAuditTrail: async (params?: { action?: string; search?: string; warehouseId?: string }): Promise<AuditEvent[]> => {
    const res = await getData('api/v1/purchase-approval/audit', params);
    if (res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  },

  // Approve single or multiple requests
  approveRequests: async (ids: string[], comments?: string) => {
    return await postData('api/v1/purchase-approval/approve', { ids, comments });
  },

  // Reject single or multiple requests
  rejectRequests: async (ids: string[], reason: string) => {
    return await postData('api/v1/purchase-approval/reject', { ids, reason });
  },

  // Send request back for revision
  sendBackRequest: async (id: string, notes: string) => {
    return await postData('api/v1/purchase-approval/send-back', { id, notes });
  },

  // Fetch configured rules
  getRules: async (): Promise<ApprovalRule[]> => {
    const res = await getData('api/v1/purchase-approval/rules');
    if (res && Array.isArray(res.data)) {
      return res.data;
    }
    return Array.isArray(res) ? res : [];
  },

  // Save rules
  saveRules: async (rules: ApprovalRule[]) => {
    return await postData('api/v1/purchase-approval/rules', { rules });
  },
};
